import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, FileText, Trash2, Send, Check, X, CalendarDays } from "lucide-react";
import { TAX_CATEGORIES } from "./adminShared";

const STATUS_CLS = {
  draft: "bg-[#6B7280]/20 text-[#C8C8CE]",
  sent: "bg-[#3B82F6]/15 text-[#93C5FD]",
  accepted: "bg-[#10B981]/15 text-[#10B981]",
  rejected: "bg-[#EF4444]/15 text-[#FCA5A5]",
  expired: "bg-[#F59E0B]/15 text-[#F59E0B]",
  converted: "bg-[#A855F7]/15 text-[#D8B4FE]",
};
const EMPTY_LINE = { description: "", amount: "", tax_category: "" };
const EMPTY = { customer_name: "", customer_phone: "", customer_email: "", event_date: "", event_time: "", party_size: "", note: "", lines: [{ ...EMPTY_LINE }] };
const kr = (n) => `${(n ?? 0).toLocaleString("sv-SE", { maximumFractionDigits: 2 })} kr`;

export default function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [statusDef, setStatusDef] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);       // create OR new version
  const [editQuote, setEditQuote] = useState(null);  // null = create
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [convertFor, setConvertFor] = useState(null);
  const [convertForm, setConvertForm] = useState({ date: "", time: "18:00", party_size: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/quotes${filter ? `?status=${filter}` : ""}`);
      setQuotes(res.data.quotes || []);
      setStatusDef(res.data.status_def || null);
    } catch { toast.error("Kunde inte ladda offerter"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const label = (s) => statusDef?.labels?.[s] || s;
  const cleanLines = (lines) => lines
    .map(l => ({ description: l.description.trim(), amount: parseFloat(l.amount) || 0,
                 tax_category: l.tax_category || "" }))
    .filter(l => l.description && l.amount);
  // Tax classification is never defaulted silently — every active line must
  // have an explicitly chosen category before saving.
  const linesUnresolved = (lines) => cleanLines(lines).some(l => !l.tax_category);

  const openCreate = () => { setEditQuote(null); setForm({ ...EMPTY, lines: [{ ...EMPTY_LINE }] }); setDialog(true); };
  const openVersion = (q) => {
    setEditQuote(q);
    const cur = q.versions[q.versions.length - 1];
    setForm({
      ...EMPTY, customer_name: q.customer_name, note: cur.note || "",
      lines: cur.lines.length ? cur.lines.map(l => ({ description: l.description, amount: String(l.amount), tax_category: l.tax_category || "" })) : [{ ...EMPTY_LINE }],
    });
    setDialog(true);
  };

  const save = async () => {
    const lines = cleanLines(form.lines);
    if (!lines.length) { toast.error("Minst en rad med beskrivning och belopp krävs"); return; }
    setSaving(true);
    try {
      if (editQuote) {
        await api.post(`/admin/quotes/${editQuote.id}/versions`, { lines, note: form.note });
        toast.success("Ny version sparad (utkast — skicka igen)");
      } else {
        await api.post("/admin/quotes", {
          ...form, lines,
          party_size: form.party_size ? parseInt(form.party_size) : undefined,
        });
        toast.success("Offert skapad");
      }
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const act = async (q, path, okMsg, body) => {
    setBusyId(q.id);
    try { await api.post(`/admin/quotes/${q.id}/${path}`, body || {}); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Misslyckades"); load(); }
    finally { setBusyId(null); }
  };

  const convert = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/quotes/${convertFor.id}/convert-to-booking`, {
        ...convertForm, party_size: convertForm.party_size ? parseInt(convertForm.party_size) : undefined,
      });
      const b = res.data.booking;
      toast.success(b.approval_required
        ? "Bokning skapad — kräver godkännande av admin under Bokningar"
        : "Bokning skapad och bekräftad");
      setConvertFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte konvertera"); }
    finally { setSaving(false); }
  };

  const row = (q) => {
    const cur = q.versions[q.versions.length - 1];
    const expired = q.status === "sent" && q.valid_until && new Date(q.valid_until) < new Date();
    return (
      <div key={q.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">
              <span className="text-[#D4B483] font-bold mr-2">{q.quote_number}</span>
              {q.customer_name}
              <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[q.status] || ""}`}>
                {expired ? "Utgången" : label(q.status)}
              </span>
              {q.versions.length > 1 && <span className="ml-2 text-[10px] text-[#9A9AA2]">v{cur.version}</span>}
            </p>
            <p className="text-[11px] text-[#9A9AA2] mt-1 flex flex-wrap gap-x-3">
              <span className="text-[#EDEDED] font-semibold">{kr(cur.total)}</span>
              {cur.vat_breakdown?.length
                ? cur.vat_breakdown.map(b => <span key={b.tax_category}>moms {b.rate}%: {kr(b.vat)}</span>)
                : <span>varav moms {kr(cur.vat_amount)}</span>}
              {q.party_size ? <span>{q.party_size} gäster</span> : null}
              {q.event_date && <span>{q.event_date}{q.event_time ? ` ${q.event_time}` : ""}</span>}
              {q.valid_until && q.status === "sent" && <span>giltig t.o.m. {q.valid_until.slice(0, 10)}</span>}
            </p>
            {cur.lines.length > 0 && (
              <p className="text-[11px] text-[#C8C8CE] mt-1 line-clamp-2">
                {cur.lines.map(l => l.description).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex gap-1.5 shrink-0 flex-wrap">
            {["draft", "sent", "expired"].includes(q.status) && (
              <Button size="sm" variant="outline" disabled={busyId === q.id} onClick={() => openVersion(q)}
                className="border-[#27272A] text-[#C8C8CE] hover:bg-[#1A1A1A] h-8 text-xs">Ny version</Button>
            )}
            {q.status === "draft" && (
              <Button size="sm" disabled={busyId === q.id || !cur.lines.length} onClick={() => act(q, "send", "Offerten är markerad som skickad")}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-8 text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Skicka</Button>
            )}
            {q.status === "sent" && (
              <>
                <Button size="sm" disabled={busyId === q.id} onClick={() => act(q, "accept", "Offerten accepterad")}
                  className="bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Accepterad</Button>
                <Button size="sm" variant="outline" disabled={busyId === q.id} onClick={() => act(q, "reject", "Offerten avböjd")}
                  className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
              </>
            )}
            {q.status === "accepted" && (
              <Button size="sm" disabled={busyId === q.id}
                onClick={() => { setConvertFor(q); setConvertForm({ date: q.event_date || "", time: q.event_time || "18:00", party_size: q.party_size ? String(q.party_size) : "" }); }}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-8 text-xs font-semibold">
                <CalendarDays className="w-3.5 h-3.5 mr-1" /> Skapa bokning</Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const active = quotes.filter(q => !["rejected", "converted"].includes(q.status));
  const done = quotes.filter(q => ["rejected", "converted"].includes(q.status));

  return (
    <div className="max-w-3xl" data-testid="quotes-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Offerter</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Versionerade offerter — skicka, acceptera och konvertera till bokning. Giltighetstid styrs under Regler.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-sm px-2">
            <option value="">Alla statusar</option>
            {(statusDef?.statuses || Object.keys(STATUS_CLS)).map(s => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <Button onClick={openCreate} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Ny offert
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : quotes.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <FileText className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga offerter{filter ? " med den statusen" : " ännu"}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {active.map(row)}
          {done.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-wider text-[#9A9AA2] pt-3">Avgjorda</p>
              <div className="opacity-60 space-y-2">{done.map(row)}</div>
            </>
          )}
        </div>
      )}

      {/* Create / new-version dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">
            {editQuote ? `Ny version — ${editQuote.quote_number}` : "Ny offert"}
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editQuote && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Namn *</Label>
                  <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
                </div>
                <div>
                  <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
                  <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
                </div>
                <div>
                  <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Datum (event)</Label>
                  <Input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
                </div>
                <div>
                  <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Antal gäster</Label>
                  <Input type="number" min="1" value={form.party_size} onChange={e => setForm(f => ({ ...f, party_size: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Rader (belopp inkl. moms)</Label>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="flex gap-2 flex-wrap">
                    <Input value={l.description} placeholder="Beskrivning"
                      onChange={e => setForm(f => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))}
                      className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] flex-1 min-w-[140px]" />
                    <Input value={l.amount} placeholder="kr" type="number"
                      onChange={e => setForm(f => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))}
                      className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-24" />
                    <select value={l.tax_category || ""} title="Momskategori (obligatorisk)"
                      onChange={e => setForm(f => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, tax_category: e.target.value } : x) }))}
                      className={`bg-[#0A0A0A] border rounded-md h-10 text-xs px-1.5 w-36 ${l.tax_category ? "border-[#27272A] text-[#C8C8CE]" : "border-[#F59E0B]/60 text-[#F59E0B]"}`}>
                      <option value="">Välj moms *</option>
                      {TAX_CATEGORIES.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                    </select>
                    <Button size="icon" variant="outline" onClick={() => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))}
                      disabled={form.lines.length === 1}
                      className="border-[#27272A] text-[#9A9AA2] hover:text-[#FCA5A5] h-10 w-10 shrink-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }))}
                className="border-[#27272A] text-[#C8C8CE] hover:bg-[#1A1A1A] mt-2 h-8 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Lägg till rad</Button>
              <p className="text-[11px] text-[#9A9AA2] mt-2">
                Summa: <span className="text-[#EDEDED] font-semibold">{kr(cleanLines(form.lines).reduce((s, l) => s + l.amount, 0))}</span>
              </p>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anteckning</Label>
              <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <Button onClick={save} disabled={saving || (!editQuote && !form.customer_name.trim()) || linesUnresolved(form.lines)}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editQuote ? "Spara ny version" : "Skapa offert"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert-to-booking dialog */}
      <Dialog open={!!convertFor} onOpenChange={(o) => !o && setConvertFor(null)}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Skapa bokning från {convertFor?.quote_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Datum *</Label>
                <Input type="date" value={convertForm.date} onChange={e => setConvertForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Tid *</Label>
                <Input type="time" value={convertForm.time} onChange={e => setConvertForm(f => ({ ...f, time: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Antal gäster</Label>
                <Input type="number" min="1" value={convertForm.party_size} onChange={e => setConvertForm(f => ({ ...f, party_size: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
            </div>
            <Button onClick={convert} disabled={saving || !convertForm.date || !convertForm.time}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Skapa bokning
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
